"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { supabase } from "@/lib/supabase";
import { useState } from "react";
import { toast } from "sonner";

export type EditFileMetadataDialogInitialValues = {
  documentDate?: string | null;
  versionLabel?: string | null;
  docCreated?: string | null;
  docLastModified?: string | null;
  revision?: string | null;
};

type FormProps = EditFileMetadataDialogInitialValues & {
  fileId: string;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

/** Inner form — mounted fresh each time fileId changes via the key prop on Dialog. */
function EditFileMetadataForm({
  fileId,
  documentDate: initialDocumentDate,
  versionLabel: initialVersionLabel,
  docCreated: initialDocCreated,
  docLastModified: initialDocLastModified,
  revision: initialRevision,
  onOpenChange,
  onSuccess,
}: FormProps) {
  const [documentDate, setDocumentDate] = useState(initialDocumentDate ?? "");
  const [versionLabel, setVersionLabel] = useState(initialVersionLabel ?? "");
  const [docCreated, setDocCreated] = useState(initialDocCreated ?? "");
  const [docLastModified, setDocLastModified] = useState(initialDocLastModified ?? "");
  const [revision, setRevision] = useState(initialRevision ?? "");
  const [submitting, setSubmitting] = useState(false);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const { error } = await supabase
          .from("files")
          .update({
            document_metadata: {
              document_date: documentDate.trim() || null,
              version_label: versionLabel.trim() || null,
              doc_created: docCreated.trim() || null,
              doc_last_modified: docLastModified.trim() || null,
              revision: revision.trim() || null,
            },
          })
          .eq("id", fileId);
        setSubmitting(false);
        if (error) {
          toast.error(error.message);
          return;
        }
        onSuccess?.();
        onOpenChange(false);
      }}
      className="flex flex-col gap-4"
    >
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-foreground">Document date</label>
        <Input
          type="date"
          value={documentDate}
          onChange={(e) => setDocumentDate(e.target.value)}
          disabled={submitting}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-foreground">Version label</label>
        <Input
          type="text"
          placeholder="e.g. v1.2"
          maxLength={50}
          value={versionLabel}
          onChange={(e) => setVersionLabel(e.target.value)}
          disabled={submitting}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-foreground">Document created</label>
        <Input
          type="date"
          value={docCreated}
          onChange={(e) => setDocCreated(e.target.value)}
          disabled={submitting}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-foreground">Document last modified</label>
        <Input
          type="date"
          value={docLastModified}
          onChange={(e) => setDocLastModified(e.target.value)}
          disabled={submitting}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-foreground">Revision</label>
        <Input
          type="text"
          placeholder="e.g. 5"
          maxLength={50}
          value={revision}
          onChange={(e) => setRevision(e.target.value)}
          disabled={submitting}
        />
      </div>
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? "Saving…" : "Save"}
        </Button>
      </DialogFooter>
    </form>
  );
}

export type EditFileMetadataDialogProps = {
  /** The `files` table primary key. */
  fileId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful save. */
  onSuccess?: () => void;
} & EditFileMetadataDialogInitialValues;

/**
 * Shared dialog for editing file metadata (document date, version label,
 * document created/last modified, revision). "Document created"/"Document
 * last modified" are the docx's own OOXML properties — deliberately labeled
 * to not be confused with the file row's own upload created/updated
 * timestamps shown elsewhere (e.g. the Details drawer). Uses a `key` prop
 * keyed to `fileId` so form state resets whenever the selected file changes
 * — no `useEffect` needed.
 */
export function EditFileMetadataDialog({
  fileId,
  open,
  onOpenChange,
  onSuccess,
  documentDate,
  versionLabel,
  docCreated,
  docLastModified,
  revision,
}: EditFileMetadataDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit file metadata</DialogTitle>
        </DialogHeader>
        {/* key={fileId} remounts the form (and resets state) when the file changes */}
        <EditFileMetadataForm
          key={fileId}
          fileId={fileId}
          documentDate={documentDate}
          versionLabel={versionLabel}
          docCreated={docCreated}
          docLastModified={docLastModified}
          revision={revision}
          onOpenChange={onOpenChange}
          onSuccess={onSuccess}
        />
      </DialogContent>
    </Dialog>
  );
}
